/**
 * Die Statusnormalisierung des Ladepfads - `migrateOrders`, index.html:5831-5835.
 *
 * ================= WARUM SIE FEHLTE UND WAS DAS KOSTETE =================
 *
 * Der Bestand fuehrt die Bestellung selbst, und JEDE geladene Bestellung lief in `load()` durch
 * diese drei Zeilen, bevor irgendeine Ansicht sie sah:
 *
 *     if (LEGACY_STATUS[o.status]) { o.status = LEGACY_STATUS[o.status]; changed = true; }
 *     if (!STATUS_MAP[o.status])   { o.status = firstStatusId();          changed = true; }
 *     for (const h of (o.history || [])) if (LEGACY_STATUS[h.status]) h.status = LEGACY_STATUS[h.status];
 *
 * Portiert war davon nur die Tabelle `LEGACY_STATUS` (`config/board.ts`) - gelesen hat sie niemand.
 *
 * Seit dem Umstieg auf JTL als Quelle (`api/jtlBestellungen.ts`, 2026-08-15) kommt der Board-Status
 * aus der Hub-Zeile, und eine Bestellung, die es nur in JTL gibt, hat gar keine. `STATUS_MAP` greift
 * dann ins Leere, und jede Ansicht faellt auf ihren eigenen Notnagel zurueck: die Statusspalte des
 * Boards zeichnet `{label: '–', color: '#666', soft: '#eee'}` mit dem Allgemein-Symbol `list`
 * (`Board/Bestellzeile.tsx`, woertlich wie index.html:7727).
 *
 * GEMESSEN am 2026-08-16 in der Produktion (`bestellhub.absina.de`, angemeldet, DOM abgefragt):
 * **95 von 95** `.order-list-status` mit `background: rgb(238,238,238)`, Text `–` und
 * `svg.lucide-list`. Keine einzige farbige Statushuelse mit ihrem Spaltensymbol. Genau das ist der
 * gemeldete Unterschied zur Cloudflare-Fassung ("die alte Loesung hatte coolere Icons").
 *
 * Der Status ist dabei nicht nur Optik: `boardGroupId`, `isArrivedStatus` und `orderStageIndex`
 * (`lib/status.ts`) lesen ihn ebenfalls - ohne ihn gruppiert das Board alles unter "Offen" und der
 * Zeitstrahl setzt jede Bestellung auf Stufe 0.
 *
 * ================= Was bewusst ANDERS ist als im Bestand =================
 *
 * Der Bestand SCHREIBT die Normalisierung zurueck (`changed = true` -> `save()`). Hier ist sie eine
 * reine Ableitung OHNE Persistenz: der Board-Status gehoert in die Hub-Zeile (`order_extras`), und
 * ein blosser Ladevorgang darf nicht ungefragt fuer jede JTL-Bestellung eine solche Zeile anlegen.
 * Sichtbar ist das Ergebnis dasselbe; sobald jemand den Status wirklich setzt, schreibt der
 * bestehende Weg (`drawerStatusAendern` in `components/App/App.tsx`).
 *
 * ================= Warum dieses Modul und nicht `lib/status.ts` =================
 *
 * `lib/` haelt die Rechenwege des Bestands, jeder mit einer Herkunftsmarke auf SEIN Symbol in
 * `index.html` (`scripts/gen-herkunft.ts` prueft das). Diese Funktion hat dort kein Gegenstueck -
 * ihre Vorlage sind drei Zeilen INNERHALB von `migrateOrders`, also der Ladevorgang selbst. Sie
 * gehoert deshalb zum Ladepfad (`state/`) und nicht zu den Rechenwegen in `lib/`.
 *
 * Aufrufer sind zwei: `Speicher.tsx` (Zweig 1, der Bestands-Server) und `lib/bestandZusammensetzen.ts`
 * (Zweig 2, die Zusammenfuehrung aus JTL und Hub-Zeilen). Die Datei gehoert damit zur Kette, die der
 * Server seit dem KI-Zugang (Stufe 1) zeichengleich kopiert (`test/lib/reineKette.test.ts`): sie darf
 * nur `.ts`-Importe innerhalb von `frontend/src` und keine Browser-Globals benutzen.
 */
import { LEGACY_STATUS } from "../config/board.ts";
import type { StatusDefinitionen } from "../lib/status.ts";
import type { Order } from "../lib/types.ts";

/**
 * `firstStatusId()` (index.html:4161) - `STATUSES[0]?.id || 'vorbereiten'`.
 *
 * Hier und nicht in `lib/status.ts`: dort ist sie ein Wert-Export und liefe damit in die
 * Woertlichkeitspruefung (`test/reference/woertlichkeit.test.ts`), die den erzeugten Rumpf Zeichen
 * fuer Zeichen gegen `index.html` haelt. Das Original liest die Modul-Globale `STATUSES`, die
 * Portierung braucht das Argument `definitionen` - eine Signaturaenderung, und die ist dort ohne
 * Eintrag in `SIGNATURAENDERUNGEN` (`scripts/gen-herkunft.ts`) rot. Diese eine Zeile deshalb hier,
 * neben `normalisierteStatus`, ihrem einzigen Aufrufer (beide stehen in dieser Datei).
 */
function ersteSpalte(definitionen: StatusDefinitionen): string {
  return definitionen.STATUSES[0]?.id || "vorbereiten";
}

/**
 * index.html:5831-5835. Liefert die Liste mit normalisiertem Status; unveraenderte Bestellungen
 * kommen als dieselbe Referenz zurueck (der Bestand aendert an Ort und Stelle, was in React nicht
 * geht - eine unnoetige neue Referenz wuerde jedes `useMemo` darueber entwerten).
 */
export function normalisierteStatus<T extends Order>(
  bestellungen: readonly T[],
  definitionen: StatusDefinitionen,
): T[] {
  const rueckfall = ersteSpalte(definitionen);
  return bestellungen.map((bestellung) => {
    // index.html:5831-5832 - erst die Alt-Kennung umschreiben, dann gegen die bekannten Spalten
    // pruefen. Die Reihenfolge traegt: `hafen` ist unbekannt und wuerde ohne den ersten Schritt auf
    // die erste Spalte fallen statt auf `zoll`.
    const abgebildet = LEGACY_STATUS[bestellung.status as string] ?? bestellung.status;
    const status = definitionen.STATUS_MAP[abgebildet as string] ? abgebildet : rueckfall;
    // index.html:5833-5835 - der Verlauf bekommt NUR die Umschreibung, KEINEN Rueckfall auf die
    // erste Spalte: ein Verlaufseintrag zu einer geloeschten Spalte bleibt im Bestand stehen.
    const verlauf = bestellung.history as { status?: unknown }[] | undefined;
    const verlaufGeaendert = Array.isArray(verlauf)
      ? verlauf.some((eintrag) => LEGACY_STATUS[eintrag?.status as string])
      : false;
    if (status === bestellung.status && !verlaufGeaendert) return bestellung;
    return {
      ...bestellung,
      status,
      ...(verlaufGeaendert
        ? {
            history: (verlauf as { status?: unknown }[]).map((eintrag) =>
              LEGACY_STATUS[eintrag?.status as string]
                ? { ...eintrag, status: LEGACY_STATUS[eintrag.status as string] }
                : eintrag,
            ),
          }
        : {}),
    };
  });
}
